import { useState } from 'react'

import { DocumentNode } from '@apollo/client'
import { Box, Link } from '@factorialco/deprecated-design-system'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import {
  CreateProjectManagementRateDocument,
  CreateProjectManagementRateMutation,
  EmployeeRatesDocument,
  UpdateProjectManagementRateDocument,
  UpdateProjectManagementRateMutation,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { InsightCard } from 'modules/project_management/components/InsightCard'
import EditRateCard from 'modules/project_management/components/ProfitabilityCards/EditRateCard'
import RateInfoCard from 'modules/project_management/components/ProfitabilityCards/RateInfoCard'
import { Timeline } from 'modules/project_management/components/Timeline'
import { SelectedEmployeeInfo } from 'modules/project_management/components/tables/JobLevelEmployeesDataCollection/types'
import { convertProfitabilityToCents } from 'modules/project_management/lib/profitabilityCalculations'

interface EmployeeSidepanelProps {
  onClose: () => void
  refetchQueries: DocumentNode[]
  showSidePanel: boolean
  selectedEmployeeInfo: SelectedEmployeeInfo
}

const EmployeeSidepanel = ({
  onClose,
  refetchQueries,
  selectedEmployeeInfo,
  showSidePanel,
}: EmployeeSidepanelProps) => {
  const [openNewRateCard, setOpenNewRateCard] = useState<boolean>(false)
  const {
    id: resourceId,
    firstName,
    lastName,
    avatar,
    billingRateCents,
    currency,
    costRate,
    profitabilityPercentageCents,
  } = selectedEmployeeInfo
  const { navigation } = useNavigation()

  const { data, loading } = useQuery(EmployeeRatesDocument, {
    variables: { employeeId: resourceId },
  })

  const [updateJobRoleRate] = useMutation(UpdateProjectManagementRateDocument, {
    extractorFn: (mutationData: UpdateProjectManagementRateMutation) =>
      mutationData.projectManagementMutations.updateProjectmanagementRate,
    awaitRefetchQueries: true,
    refetchQueries: [...new Set([EmployeeRatesDocument, ...refetchQueries])],
  })

  const [createJobRoleRate] = useMutation(CreateProjectManagementRateDocument, {
    extractorFn: (mutationData: CreateProjectManagementRateMutation) =>
      mutationData.projectManagementMutations.createProjectmanagementRate,
    awaitRefetchQueries: true,
    refetchQueries: [...new Set([EmployeeRatesDocument, ...refetchQueries])],
  })

  const referenceRate = data?.projectManagement?.employeeRateslist?.nodes
    ?.slice()
    .sort((a, b) => new Date(b.startsOn).getTime() - new Date(a.startsOn).getTime())
    .find((rate) => !rate.unlinked)
  const referenceBillingRate = referenceRate?.billingRateCents
  const referenceProfitMargin = referenceRate?.profitabilityPercentageCents
  const rates = data?.projectManagement?.employeeRateslist?.nodes
  const activeRate = rates?.find((rate) => rate.endsOn === null)
  const defaultRateCost = data?.projectManagement?.defaultRateCostsConnection?.nodes?.find(
    (cost) => cost.resourceId === resourceId
  )?.chargingCostCents

  const handleUpdateEmployeeRate = async (values: {
    billRate: string
    minProfitMargin: string
  }) => {
    track(trackingEvents.projectManagement.jobsAndRates.clickOnSaveNewEmployeeRate)

    const newBillingRateCents = Math.round(Number(values.billRate || '0') * 100)

    const newProfitabilityPercentageCents = values.minProfitMargin
      ? convertProfitabilityToCents(Number(values.minProfitMargin))
      : 0

    if (activeRate) {
      const response = await updateJobRoleRate({
        variables: {
          id: activeRate.id,
          billingRateCents: newBillingRateCents,
          profitabilityPercentageCents: newProfitabilityPercentageCents,
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        onClose()
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
    } else {
      if (currency == '') {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
        return
      }

      const response = await createJobRoleRate({
        variables: {
          resourceId: resourceId,
          resourceType: 'Employee',
          referenceRateId: referenceRate?.id,
          billingRateCents: newBillingRateCents,
          profitabilityPercentageCents: newProfitabilityPercentageCents,
          currency,
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
    }
  }

  const hasReferenceBillingRate =
    referenceBillingRate !== undefined && referenceBillingRate !== null
  const hasReferenceProfitMargin =
    referenceProfitMargin !== undefined && referenceProfitMargin !== null

  return (
    <Sidepanel
      boxPadding={false}
      open={showSidePanel}
      onClose={onClose}
      title={i18n.t('project_management.people_and_rates.sidepanel.title.edit_rates')}
      width="wide"
    >
      <ResourceHeader
        avatar={{
          type: 'person',
          firstName: firstName,
          lastName: lastName,
          src: buildUrl(avatar),
        }}
        title={`${firstName} ${lastName}`}
        primaryAction={{
          label: i18n.t('project_management.jobs_and_rates.sidepanels.new_rate_button'),
          icon: Plus,
          disabled: openNewRateCard,
          onClick: () => {
            track(trackingEvents.projectManagement.jobsAndRates.clickOnCreateNewEmployeeRate)
            setOpenNewRateCard(true)
          },
        }}
        metadata={
          !!defaultRateCost
            ? undefined
            : [
                {
                  label: i18n.t('project_management.headers.cost_rate'),
                  value: {
                    type: 'status' as const,
                    label: i18n.t('project_management.project_workers.details.cost_missing.title'),
                    variant: 'info' as const,
                  },
                  info: {
                    title: i18n.t(
                      'project_management.project_workers.details.cost_missing.tooltip_message'
                    ),
                  },
                },
              ]
        }
      />

      <Box paddingX="s24" paddingTop="s8" gap="s12">
        {loading ? (
          <InsightCard.Skeleton title="" withStackedBar />
        ) : (
          <>
            <Timeline>
              {openNewRateCard && (
                <EditRateCard
                  billingRateCents={billingRateCents / 100}
                  currency={currency}
                  referenceBillingRate={
                    hasReferenceBillingRate ? referenceBillingRate / 100 : undefined
                  }
                  referenceProfitMargin={
                    hasReferenceProfitMargin ? referenceProfitMargin / 100 : undefined
                  }
                  maxCostRate={costRate / 100}
                  initialProfitMargin={profitabilityPercentageCents}
                  onClose={() => setOpenNewRateCard(false)}
                  onSubmit={(values) => {
                    handleUpdateEmployeeRate(values)
                  }}
                />
              )}
              {rates &&
                rates?.length > 0 &&
                rates
                  .slice()
                  .sort((a, b) => new Date(b.startsOn).getTime() - new Date(a.startsOn).getTime())
                  .map((rate, index) => (
                    <RateInfoCard
                      key={index}
                      startsOn={rate.startsOn}
                      endsOn={rate.endsOn}
                      currency={rate.currency}
                      chargingCostCents={rate.chargingCostCents}
                      billingRateCents={rate.billingRateCents}
                      profitabilityPercentageCents={rate.profitabilityPercentageCents}
                      rateType={rate.unlinked ? 'custom' : 'job'}
                    />
                  ))}
            </Timeline>
            {!rates?.length && !hasReferenceBillingRate && !openNewRateCard && (
              <Alert variant="warning">
                <AlertTitle>
                  {i18n.t(
                    'project_management.people_and_rates.sidepanel.alerts.no_reference_bill_rate.title'
                  )}
                </AlertTitle>
                <AlertDescription>
                  {i18n.t(
                    'project_management.people_and_rates.sidepanel.alerts.no_reference_bill_rate.description'
                  )}
                </AlertDescription>
                <Link.Router
                  type="solid"
                  to={`${navigation.projects.jobsAndRates.list.fullPath()}?openAddSidepanel=true`}
                >
                  {i18n.t(
                    'project_management.people_and_rates.sidepanel.alerts.no_reference_bill_rate.link'
                  )}
                </Link.Router>
              </Alert>
            )}
          </>
        )}
      </Box>
    </Sidepanel>
  )
}

export default EmployeeSidepanel
